import { FinancialStatementPart } from '../../models/common/FinancialStatementPart';
import { FinancialStatementAccount } from '../../models/common/FinancialStatementAccount';
import { FsSums } from '../../utils/common/FsSums';
import { FsPeriodsFormatterService } from './fs-periods-formatter/fs-periods-formatter.service';

export class FsCalculationsService {

  // accepts accounts for which to calculate sums and glData to calculate from, filters by account numbers and sums
  static calculateAccountsTotals(accounts: FinancialStatementAccount[], fsYear: number, startDate?: Date, endDate?: Date): void {
    accounts.forEach((account) => {
      account.groupByQuarters();
      account.findAccountSums(fsYear, startDate, endDate);
    });
  }

  // anytime an account total is changed, the item total and the part total will be changed so there is no sense to break this down further
  static calculatePartsBalances(fsParts: FinancialStatementPart[], selectedBSDateColumns?: string[]): void {
    if (!fsParts) {
      return;
    }
    fsParts?.forEach((part) => {
      part.financialStatementItems?.forEach((item) => {
        item.findSums(selectedBSDateColumns);
        item.sumManualEntriesForItem();
        if (item.assetsAndLiabilities) {
          const totalBalance = item.sums.sumITP + item.sums.manualDebit - item.sums.manualCredit;
          if (totalBalance < 0 && (item.partName === 'Assets' || item.partName === 'Profits')) {
            const originPart = fsParts.find((fsPart: FinancialStatementPart) =>
              (item.partName === 'Assets' ? fsPart.partName === 'Assets' : fsPart.partName === 'Profits'));
            const destPart = fsParts.find((fsPart: FinancialStatementPart) =>
              (item.partName === 'Assets' ? fsPart.partName === 'Liabilities' : fsPart.partName === 'Losses'));
            item.partName = 'Liabilities';
            originPart.financialStatementItems = originPart.financialStatementItems.filter((partItem) => item.id !== partItem.id);
            destPart.financialStatementItems.push(item);
          } else if (totalBalance > 0 && (item.partName === 'Liabilities' || item.partName === 'Losses')) {
            const originPart = fsParts.find((fsPart: FinancialStatementPart) =>
              (item.partName === 'Liabilities' ? fsPart.partName === 'Liabilities' : fsPart.partName === 'Losses'));
            const destPart = fsParts.find((fsPart: FinancialStatementPart) =>
              (item.partName === 'Liabilities' ? fsPart.partName === 'Assets' : fsPart.partName === 'Profits'));
            item.partName = 'Assets';
            originPart.financialStatementItems = originPart.financialStatementItems.filter((partItem) => item.id !== partItem.id);
            destPart.financialStatementItems.push(item);
          }
        }
      });
      part.findSums(selectedBSDateColumns);
      part.sumManualEntriesForPart();
    });
  }

  static calculateNetTotals(netTotals: FsSums, parts: FinancialStatementPart[], isPL: boolean, selectedBSDateColumns?: string[]): void {
    if (!parts) {
      return;
    }
    netTotals.resetAllSums();
    netTotals.sumITP = isPL ?
      Number((parts[0].sums.sumITP + parts[1].sums.sumITP).toFixed(0)) :
      Number((parts[0].sums.sumITP + parts[1].sums.sumITP + parts[2].sums.sumITP).toFixed(0));
    netTotals.sumITPrevYear = isPL ?
      Number((parts[0].sums.sumITPrevYear + parts[1].sums.sumITPrevYear).toFixed(0)) :
      Number((parts[0].sums.sumITPrevYear + parts[1].sums.sumITPrevYear + parts[2].sums.sumITPrevYear).toFixed(0));
    netTotals.sumITYearBeforePrev = isPL ?
      Number((parts[0].sums.sumITYearBeforePrev + parts[1].sums.sumITYearBeforePrev).toFixed(0)) :
      Number((parts[0].sums.sumITYearBeforePrev + parts[1].sums.sumITYearBeforePrev + parts[2].sums.sumITYearBeforePrev).toFixed(0));
    netTotals.manualCredit = isPL ?
      Number((parts[0].sums.manualCredit + parts[1].sums.manualCredit).toFixed(0)) :
      Number((parts[0].sums.manualCredit + parts[1].sums.manualCredit + parts[2].sums.manualCredit).toFixed(0));
    netTotals.manualDebit = isPL ?
      Number((parts[0].sums.manualDebit + parts[1].sums.manualDebit).toFixed(0)) :
      Number((parts[0].sums.manualDebit + parts[1].sums.manualDebit + parts[2].sums.manualDebit).toFixed(0));
    netTotals.balance = isPL ?
      Number((parts[0].sums.balance + parts[1].sums.balance).toFixed(0)) :
      Number((parts[0].sums.balance + parts[1].sums.balance + parts[2].sums.balance).toFixed(0));

    if (isPL) {
      FsPeriodsFormatterService.getAllExistingPeriodsFromParts(parts).forEach((quarter) => {
        const profitSum = parts[0].sums.quarterlySums.get(quarter) || 0;
        const lossSum = parts[1].sums.quarterlySums.get(quarter) || 0;
        netTotals.quarterlySums.set(quarter, profitSum + lossSum);
      });
    } else {
      parts.forEach((part) => {
        part.sums.quarterlySums.forEach((itemSum, quarter) => {
          const currentQuarterSum = part.sums.quarterlySums.get(quarter) || 0;
          const newQuarterSum = currentQuarterSum + itemSum;
          netTotals.quarterlySums.set(quarter, newQuarterSum);
        });
      });
    }
    netTotals.calculateYearlySums();

    if (selectedBSDateColumns) {
      netTotals.setDateColumnSumsKeys(selectedBSDateColumns);
      for (const part of parts) {
        for (const [columnDate, columnValue] of part.sums.dateColumnSums) {
          const currentValue = netTotals.dateColumnSums.get(columnDate);
          netTotals.dateColumnSums.set(columnDate, (currentValue ?? 0) + columnValue);
        }
      }
    }
  }

  static calculateBalanceSheetTotals(
    netTotals: FsSums, parts: FinancialStatementPart[], isPL: boolean, selectedBSDateColumns?: string[],
  ): void {
    if (!parts) {
      return;
    }
    if (!netTotals) {
      netTotals = new FsSums();
    }
    this.calculatePartsBalances(parts, selectedBSDateColumns);
    this.calculateNetTotals(netTotals, parts, isPL, selectedBSDateColumns);
  }

  static calculateUnallocatedSums(sums: FsSums, accounts: FinancialStatementAccount[]): void {
    sums.resetAllSums();
    accounts.forEach((account) => {
      sums.balance += account.sums.balance;
      sums.sumITP += account.sums.sumITP;
      sums.sumITPrevYear += account.sums.sumITPrevYear;
      sums.sumITYearBeforePrev += account.sums.sumITYearBeforePrev;
      sums.manualCredit += account.sums.manualCredit;
      sums.manualDebit += account.sums.manualDebit;
    });
  }
}
